import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { applyCouponApi, getAvailableCouponsApi } from "../utils/couponApi";

const PromoCodeSection = ({
  cartItems = [],
  appliedCoupon = null,
  onApplyCoupon = () => {},
  onRemoveCoupon = () => {},
  disabled = false,
  shippingState = "",
  customerId = null,
}) => {
  const [couponInput, setCouponInput] = useState("");
  const [loadingApply, setLoadingApply] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");
  const [authPromptCoupon, setAuthPromptCoupon] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to check if current user is authenticated
  const isAuthenticated = Boolean(user && (user.id || user.customer_details?.id) && sessionStorage.getItem("access"));

  // ── Auto-apply pending coupon after login / register ──────────────────
  useEffect(() => {
    const pendingCode = sessionStorage.getItem("pending_coupon_code");
    if (pendingCode && isAuthenticated && cartItems.length > 0 && !appliedCoupon) {
      sessionStorage.removeItem("pending_coupon_code");
      toast(`Applying your saved promo code "${pendingCode}"...`, { icon: "🎁" });
      handleApply(pendingCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, cartItems.length, appliedCoupon]);

  // ── Fetch active promo codes ──────────────────────────────────────────
  const fetchCoupons = useCallback(async () => {
    try {
      setLoadingCoupons(true);
      const coupons = await getAvailableCouponsApi();
      setAvailableCoupons(Array.isArray(coupons) ? coupons : []);
    } catch (err) {
      console.error("Failed to load available coupons:", err);
    } finally {
      setLoadingCoupons(false);
    }
  }, []);

  const openDrawer = () => {
    setShowDrawer(true);
    fetchCoupons();
  };

  const closeDrawer = () => {
    setShowDrawer(false);
  };

  // ── Apply promo code ─────────────────────────────────────────────────
  const handleApply = async (codeToApply) => {
    const rawCode = codeToApply || couponInput;
    const code = (rawCode || "").trim().toUpperCase();

    if (!code) {
      toast.error("Please enter a valid promo code");
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      toast.error("Your cart is empty. Add items before applying a promo code.");
      return;
    }

    try {
      setLoadingApply(true);
      const activeCustomerId = customerId || user?.customer_details?.id || user?.id || null;
      const res = await applyCouponApi(code, cartItems, shippingState, activeCustomerId);

      if (res.status === false) {
        handleApplyFailure(code, res.message || "Invalid or ineligible promo code");
        return;
      }

      // Format response data cleanly
      const payloadData = res.data || res;
      const discountVal = Number(
        payloadData.discount_amount != null
          ? payloadData.discount_amount
          : res.discount_amount || res.discount || 0
      );

      const couponData = {
        code: payloadData.coupon_code || code,
        title: payloadData.title || res.title || `${code} Applied`,
        description: payloadData.description || res.description || "",
        discount_amount: discountVal,
        discount_type: (payloadData.discount_type || res.discount_type || "flat").toLowerCase(),
        discount_value: payloadData.discount_value || res.discount_value,
        max_discount_amount: payloadData.max_discount_amount || res.max_discount_amount,
        first_time_user_only: Boolean(payloadData.first_time_user_only ?? res.first_time_user_only),
        discounted_subtotal: payloadData.discounted_subtotal || res.discounted_subtotal,
        final_total: payloadData.final_total || res.final_total,
      };

      onApplyCoupon(couponData);
      setCouponInput("");
      setShowDrawer(false);
      setAuthPromptCoupon(null);

      const savingText = discountVal > 0 ? ` (Saved ₹${Math.round(discountVal).toLocaleString()})` : "";
      toast.success(res.message || `Promo code "${code}" applied successfully!${savingText} 🎉`, {
        duration: 4000,
      });
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Failed to apply promo code. Please try again.";

      handleApplyFailure(code, errorMsg);
    } finally {
      setLoadingApply(false);
    }
  };

  // ── Handle error messages with tailored UX flows ─────────────────────
  const handleApplyFailure = (code, message) => {
    const lower = (message || "").toLowerCase();

    // Case A: First-time coupon attempted by unauthenticated guest user
    if (
      lower.includes("only available for first-time users") ||
      lower.includes("sign in to apply") ||
      lower.includes("please sign in") ||
      lower.includes("login to apply")
    ) {
      setAuthPromptCoupon({
        code: code,
        message: message,
      });
      return;
    }

    // Case B: First-time coupon attempted by user with prior completed orders
    if (lower.includes("first-time users on their first order") || lower.includes("first order only")) {
      toast.error(message, {
        duration: 5000,
        icon: "🔒",
      });
      return;
    }

    // Case C: Minimum order value not met or usage limit exceeded
    toast.error(message, { duration: 4000 });
  };

  const handleRemove = () => {
    onRemoveCoupon();
    toast.success("Promo code removed", { icon: "🗑️" });
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(`Code "${code}" copied to clipboard!`, { duration: 2000 });
    setTimeout(() => setCopiedCode(""), 2200);
  };

  // ── Redirect user to login with return intent ─────────────────────────
  const handleSignInToClaim = (couponCode) => {
    const code = couponCode || authPromptCoupon?.code;
    if (code) {
      sessionStorage.setItem("pending_coupon_code", code);
    }
    sessionStorage.setItem(
      "login_redirect",
      JSON.stringify({
        pathname: location.pathname,
        search: location.search,
        state: location.state,
      })
    );
    sessionStorage.setItem(
      "redirect_toast",
      `Welcome! You can now use promo code "${code}" on your first purchase.`
    );
    navigate("/login");
  };

  const handleRegisterToClaim = (couponCode) => {
    const code = couponCode || authPromptCoupon?.code;
    if (code) {
      sessionStorage.setItem("pending_coupon_code", code);
    }
    sessionStorage.setItem(
      "login_redirect",
      JSON.stringify({
        pathname: location.pathname,
        search: location.search,
        state: location.state,
      })
    );
    navigate("/register");
  };

  return (
    <div className="promo-system-root mb-4">
      {/* ────────────────────────────────────────────────────────────── */}
      {/* 1. APPLIED STATE CARD                                          */}
      {/* ────────────────────────────────────────────────────────────── */}
      {appliedCoupon ? (
        <div className="applied-voucher-card shadow-sm">
          <div className="applied-voucher-edge">
            <span className="voucher-scissors">✂</span>
          </div>

          <div className="applied-voucher-content">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <div className="applied-check-icon">✓</div>
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="applied-code-pill">{appliedCoupon.code}</span>
                    <span className="badge-applied-status">APPLIED</span>
                    {appliedCoupon.first_time_user_only && (
                      <span className="badge-first-time-pill">
                        ⭐ FIRST ORDER SPECIAL
                      </span>
                    )}
                  </div>
                  <div className="applied-savings-text">
                    {appliedCoupon.discount_amount > 0 ? (
                      <>You are saving <strong>₹{Math.round(appliedCoupon.discount_amount).toLocaleString()}</strong> with this coupon!</>
                    ) : (
                      <>Special promo discount activated!</>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn-remove-coupon"
                onClick={handleRemove}
                disabled={disabled}
                title="Remove applied coupon"
              >
                ✕ Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ──────────────────────────────────────────────────────────── */
        /* 2. PROMO CODE INPUT CARD                                     */
        /* ──────────────────────────────────────────────────────────── */
        <div className="promo-input-card shadow-sm">
          <div className="promo-card-header d-flex justify-content-between align-items-center mb-2">
            <div className="promo-card-title d-flex align-items-center gap-2">
              <span className="promo-ticket-icon">🎟️</span>
              <span className="fw-bold">Have a Promo Code?</span>
            </div>
            <button
              type="button"
              className="btn-view-coupons-link"
              onClick={openDrawer}
            >
              🏷️ View Available Coupons
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleApply();
            }}
            className="promo-input-group"
          >
            <div className="promo-input-wrapper">
              <input
                type="text"
                className="promo-input-field text-uppercase"
                placeholder="ENTER PROMO CODE (e.g. WELCOME100)"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                disabled={disabled || loadingApply}
                maxLength={25}
                autoComplete="off"
                spellCheck="false"
              />
              {couponInput && !loadingApply && (
                <button
                  type="button"
                  className="promo-clear-btn"
                  onClick={() => setCouponInput("")}
                  title="Clear input"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="submit"
              className={`btn-apply-main ${loadingApply ? "is-loading" : ""}`}
              disabled={disabled || loadingApply || !couponInput.trim()}
            >
              {loadingApply ? (
                <span className="d-flex align-items-center gap-2">
                  <span className="spinner-border spinner-border-sm" role="status" />
                  Applying...
                </span>
              ) : (
                <span>Apply</span>
              )}
            </button>
          </form>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 3. AVAILABLE COUPONS DRAWER / MODAL                            */}
      {/* ────────────────────────────────────────────────────────────── */}
      {showDrawer && (
        <div className="drawer-backdrop" onClick={closeDrawer}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            {/* Drawer Header */}
            <div className="drawer-header">
              <div className="d-flex align-items-center gap-2">
                <span className="drawer-header-icon">🎁</span>
                <div>
                  <h5 className="drawer-title m-0">Coupons & Exclusive Offers</h5>
                  <p className="drawer-subtitle m-0">
                    Apply promo codes for extra savings on your order
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="drawer-close-btn"
                onClick={closeDrawer}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Quick Promo Input inside Drawer */}
            <div className="drawer-quick-apply">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control text-uppercase drawer-input"
                  placeholder="HAVE ANOTHER CODE? ENTER HERE"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  disabled={loadingApply}
                  maxLength={25}
                />
                <button
                  type="button"
                  className="btn btn-apply-drawer"
                  disabled={loadingApply || !couponInput.trim()}
                  onClick={() => handleApply()}
                >
                  {loadingApply ? "Applying..." : "Apply"}
                </button>
              </div>
            </div>

            {/* Drawer Body: Coupons List */}
            <div className="drawer-body">
              {loadingCoupons ? (
                <div className="drawer-loader text-center py-5">
                  <div className="spinner-border text-success" role="status" />
                  <p className="mt-3 text-muted fw-semibold">Loading available offers...</p>
                </div>
              ) : availableCoupons.length === 0 ? (
                <div className="drawer-empty text-center py-5">
                  <span style={{ fontSize: "2.5rem" }}>🏷️</span>
                  <h6 className="fw-bold mt-3 text-theme-dark">No Active Coupons Right Now</h6>
                  <p className="text-muted small px-4">
                    Check back soon for seasonal specials and festive discount codes!
                  </p>
                </div>
              ) : (
                <div className="coupons-list d-flex flex-column gap-3">
                  {availableCoupons.map((coupon) => {
                    const isApplied = appliedCoupon?.code === coupon.code;
                    const isFirstTime = Boolean(coupon.first_time_user_only);
                    const canUse = coupon.can_use !== false;
                    const reason = coupon.cannot_use_reason || coupon.reason || "";
                    const reasonLower = reason.toLowerCase();

                    // Determine if login is required to claim
                    const requiresLogin =
                      !isAuthenticated &&
                      (isFirstTime || reasonLower.includes("sign in") || reasonLower.includes("log in"));

                    // Determine if existing user is ineligible for first time coupon
                    const isPriorOrderIneligible =
                      isFirstTime &&
                      !canUse &&
                      (reasonLower.includes("first-time users on their first order") ||
                        reasonLower.includes("first order only") ||
                        reasonLower.includes("previous order"));

                    return (
                      <div
                        key={coupon.id || coupon.code}
                        className={`voucher-ticket ${
                          isApplied
                            ? "is-applied"
                            : canUse
                            ? "is-eligible"
                            : requiresLogin
                            ? "requires-login"
                            : "is-ineligible"
                        }`}
                      >
                        {/* Perforated Notch Elements */}
                        <div className="voucher-notch notch-left"></div>
                        <div className="voucher-notch notch-right"></div>

                        {/* Top Badges */}
                        <div className="voucher-header d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="voucher-code-chip">{coupon.code}</span>
                            <button
                              type="button"
                              className="btn-copy-code"
                              onClick={() => handleCopyCode(coupon.code)}
                              title="Copy promo code"
                            >
                              {copiedCode === coupon.code ? "✓ Copied" : "📋 Copy"}
                            </button>
                          </div>

                          {/* Shimmering Badge for First-Time User */}
                          {isFirstTime && (
                            <span className="badge-shimmer-first-order">
                              ⭐ FIRST ORDER EXCLUSIVE
                            </span>
                          )}
                        </div>

                        {/* Discount Headline */}
                        <div className="voucher-headline">
                          {String(coupon.discount_type).toLowerCase() === "percentage"
                            ? `${coupon.discount_value}% OFF`
                            : `FLAT ₹${Number(coupon.discount_value || 0).toLocaleString()} OFF`}
                          {coupon.max_discount_amount && String(coupon.discount_type).toLowerCase() === "percentage" && (
                            <span className="voucher-max-cap">
                              (Up to ₹{Number(coupon.max_discount_amount).toLocaleString()})
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        {coupon.description && (
                          <div className="voucher-description text-secondary mb-2">
                            {coupon.description}
                          </div>
                        )}

                        {/* Terms & Min Order */}
                        <div className="voucher-meta d-flex justify-content-between align-items-center flex-wrap pt-2">
                          <span className="meta-item">
                            {coupon.min_order_value > 0
                              ? `Min. Order: ₹${Number(coupon.min_order_value).toLocaleString()}`
                              : "No Minimum Order"}
                          </span>
                          {coupon.end_date && (
                            <span className="meta-item">
                              Valid till: {new Date(coupon.end_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {/* Ineligibility Reason Banner */}
                        {!canUse && reason && (
                          <div className="voucher-reason-banner mt-2">
                            <span className="reason-icon">🔒</span>
                            <span className="reason-text">{reason}</span>
                          </div>
                        )}

                        {/* Action CTA */}
                        <div className="voucher-cta-footer mt-3 pt-2 d-flex justify-content-between align-items-center">
                          {isApplied ? (
                            <div className="d-flex align-items-center gap-1 text-success fw-bold small">
                              <span>✓</span> Currently Applied
                            </div>
                          ) : isPriorOrderIneligible ? (
                            <div className="badge-ineligible-tag">
                              🔒 For First Orders Only
                            </div>
                          ) : requiresLogin ? (
                            <div className="text-warning-emphasis small fw-semibold">
                              🔐 First-time special — sign in required
                            </div>
                          ) : (
                            <div className="text-muted small">
                              {coupon.min_order_value > 0 ? "Check cart subtotal to apply" : "Ready to apply"}
                            </div>
                          )}

                          {/* CTA Button */}
                          {isApplied ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger py-1 px-3 rounded-pill"
                              onClick={handleRemove}
                            >
                              Remove
                            </button>
                          ) : requiresLogin ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-signin-claim px-3 py-1 rounded-pill"
                              onClick={() => handleSignInToClaim(coupon.code)}
                            >
                              🔑 Sign In to Claim
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-apply-voucher px-4 py-1 rounded-pill"
                              disabled={!canUse || loadingApply}
                              onClick={() => handleApply(coupon.code)}
                            >
                              APPLY
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="drawer-footer">
              <button
                type="button"
                className="btn btn-drawer-dismiss w-100 py-2 rounded-pill"
                onClick={closeDrawer}
              >
                Close Offers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 4. "SIGN IN TO APPLY" MODAL (FOR FIRST-TIME USERS)              */}
      {/* ────────────────────────────────────────────────────────────── */}
      {authPromptCoupon && (
        <div className="auth-prompt-backdrop" onClick={() => setAuthPromptCoupon(null)}>
          <div className="auth-prompt-card shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="auth-prompt-badge">
              <span>🎉 NEW USER SPECIAL</span>
            </div>

            <div className="auth-prompt-icon-wrap">
              <span className="auth-prompt-icon">🎁</span>
            </div>

            <h5 className="auth-prompt-title">Sign In to Claim Your Welcome Offer</h5>
            <p className="auth-prompt-desc">
              Promo code <strong>"{authPromptCoupon.code}"</strong> is exclusively crafted for first-time customers on their very first order.
            </p>
            <p className="auth-prompt-subdesc text-muted small">
              Please sign in or create a quick account so we can apply this special welcome discount to your cart!
            </p>

            <div className="auth-prompt-actions d-flex flex-column gap-2 mt-4">
              <button
                type="button"
                className="btn btn-auth-primary py-2 rounded-pill fw-bold"
                onClick={() => handleSignInToClaim(authPromptCoupon.code)}
              >
                Sign In to Apply Promo
              </button>
              <button
                type="button"
                className="btn btn-auth-secondary py-2 rounded-pill fw-semibold"
                onClick={() => handleRegisterToClaim(authPromptCoupon.code)}
              >
                Create an Account
              </button>
              <button
                type="button"
                className="btn btn-link text-muted small p-1 mt-1 text-decoration-none"
                onClick={() => setAuthPromptCoupon(null)}
              >
                Continue as Guest
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────── */}
      {/* 5. PREMIUM COMPONENT STYLING                                  */}
      {/* ────────────────────────────────────────────────────────────── */}
      <style>{`
        /* Root container */
        .promo-system-root {
          font-family: inherit;
        }

        /* ── Applied Voucher Card ── */
        .applied-voucher-card {
          background: linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%);
          border: 1.5px dashed #2E7D32;
          border-radius: 14px;
          position: relative;
          overflow: hidden;
          padding: 14px 16px;
          animation: slideDown 0.25s ease-out;
        }
        .applied-check-icon {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #2E7D32;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 0.9rem;
          flex-shrink: 0;
        }
        .applied-code-pill {
          font-family: monospace;
          font-weight: 700;
          font-size: 1rem;
          color: #1b5e20;
          background: #c8e6c9;
          padding: 2px 10px;
          border-radius: 6px;
          letter-spacing: 0.8px;
        }
        .badge-applied-status {
          font-size: 0.7rem;
          font-weight: 700;
          background: #2E7D32;
          color: white;
          padding: 3px 8px;
          border-radius: 12px;
          letter-spacing: 0.5px;
        }
        .badge-first-time-pill {
          font-size: 0.7rem;
          font-weight: 800;
          background: linear-gradient(90deg, #ff8a00, #e52e71);
          color: white;
          padding: 3px 8px;
          border-radius: 12px;
          letter-spacing: 0.5px;
        }
        .applied-savings-text {
          font-size: 0.86rem;
          color: #2E7D32;
          margin-top: 4px;
        }
        .btn-remove-coupon {
          background: transparent;
          border: 1.5px solid #d32f2f;
          color: #d32f2f;
          font-size: 0.78rem;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 20px;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        .btn-remove-coupon:hover:not(:disabled) {
          background: #d32f2f;
          color: white;
        }

        /* ── Promo Code Input Card ── */
        .promo-input-card {
          background: #ffffff;
          border: 1.5px solid #e0e0e0;
          border-radius: 14px;
          padding: 16px 18px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .promo-input-card:hover {
          border-color: #c8e6c9;
          box-shadow: 0 4px 14px rgba(46, 125, 50, 0.08) !important;
        }
        .promo-ticket-icon {
          font-size: 1.15rem;
        }
        .promo-card-title {
          font-size: 0.95rem;
          color: #2E7D32;
        }
        .btn-view-coupons-link {
          background: transparent;
          border: none;
          color: #8A4B27;
          font-weight: 700;
          font-size: 0.82rem;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s ease;
        }
        .btn-view-coupons-link:hover {
          color: #2E7D32;
          text-decoration: underline;
        }

        .promo-input-group {
          display: flex;
          gap: 8px;
          margin-top: 4px;
        }
        .promo-input-wrapper {
          position: relative;
          flex: 1;
        }
        .promo-input-field {
          width: 100%;
          height: 44px;
          font-family: monospace;
          font-weight: 700;
          font-size: 0.92rem;
          letter-spacing: 0.8px;
          border: 1.5px solid #d6d6d6;
          border-radius: 10px;
          padding: 0 34px 0 14px;
          transition: all 0.2s ease;
          background: #fdfdfd;
        }
        .promo-input-field:focus {
          outline: none;
          border-color: #2E7D32;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.15);
        }
        .promo-clear-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #9e9e9e;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 4px;
        }
        .promo-clear-btn:hover {
          color: #424242;
        }

        .btn-apply-main {
          height: 44px;
          padding: 0 22px;
          background: #2E7D32;
          color: white;
          font-weight: 700;
          font-size: 0.9rem;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .btn-apply-main:hover:not(:disabled) {
          background: #1b5e20;
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(46, 125, 50, 0.25);
        }
        .btn-apply-main:disabled {
          background: #b0bec5;
          cursor: not-allowed;
          opacity: 0.7;
        }

        /* ── Slide-Over Drawer / Modal ── */
        .drawer-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(18, 18, 18, 0.55);
          backdrop-filter: blur(4px);
          z-index: 99999;
          display: flex;
          justify-content: flex-end;
          animation: fadeIn 0.2s ease-out;
        }
        .drawer-panel {
          width: 100%;
          max-width: 480px;
          height: 100vh;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          box-shadow: -8px 0 30px rgba(0, 0, 0, 0.25);
          animation: slideInRight 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .drawer-header {
          padding: 18px 22px;
          background: #FAF7F2;
          border-bottom: 1.5px solid #ece7df;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .drawer-header-icon {
          font-size: 1.6rem;
        }
        .drawer-title {
          font-size: 1.12rem;
          color: #2E7D32;
          font-weight: 700;
        }
        .drawer-subtitle {
          font-size: 0.78rem;
          color: #7a563a;
        }
        .drawer-close-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #eee;
          border: none;
          color: #555;
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }
        .drawer-close-btn:hover {
          background: #ddd;
          color: #111;
        }

        .drawer-quick-apply {
          padding: 14px 20px;
          background: #fbfbfb;
          border-bottom: 1px solid #eee;
        }
        .drawer-input {
          font-family: monospace;
          font-weight: 700;
          font-size: 0.88rem;
          letter-spacing: 0.8px;
        }
        .btn-apply-drawer {
          background: #2E7D32;
          color: white;
          font-weight: 700;
          font-size: 0.85rem;
          border: none;
        }
        .btn-apply-drawer:hover:not(:disabled) {
          background: #1b5e20;
          color: white;
        }

        .drawer-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #f7f6f2;
        }
        .drawer-footer {
          padding: 14px 20px;
          border-top: 1px solid #eee;
          background: #ffffff;
        }
        .btn-drawer-dismiss {
          background: #f0f0f0;
          color: #424242;
          font-weight: 700;
          border: none;
          font-size: 0.9rem;
          transition: background 0.2s ease;
        }
        .btn-drawer-dismiss:hover {
          background: #e0e0e0;
        }

        /* ── Perforated Voucher Card ── */
        .voucher-ticket {
          background: #ffffff;
          border: 1.5px dashed #c8e6c9;
          border-radius: 12px;
          padding: 16px 20px;
          position: relative;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease;
        }
        .voucher-ticket.is-eligible:hover {
          border-color: #2E7D32;
          box-shadow: 0 4px 14px rgba(46, 125, 50, 0.12);
        }
        .voucher-ticket.is-applied {
          background: #f1f8e9;
          border-color: #2E7D32;
        }
        .voucher-ticket.requires-login {
          border-color: #ffe082;
          background: #fffdf5;
        }
        .voucher-ticket.is-ineligible {
          opacity: 0.65;
          background: #fafafa;
          border-color: #e0e0e0;
        }

        /* Perforated edge notches */
        .voucher-notch {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 14px;
          height: 22px;
          background: #f7f6f2;
          z-index: 2;
        }
        .notch-left {
          left: -1px;
          border-radius: 0 14px 14px 0;
          border: 1.5px dashed #c8e6c9;
          border-left: none;
        }
        .notch-right {
          right: -1px;
          border-radius: 14px 0 0 14px;
          border: 1.5px dashed #c8e6c9;
          border-right: none;
        }
        .voucher-ticket.is-ineligible .notch-left,
        .voucher-ticket.is-ineligible .notch-right {
          border-color: #e0e0e0;
        }

        .voucher-code-chip {
          font-family: monospace;
          font-weight: 800;
          font-size: 0.95rem;
          color: #2E7D32;
          background: #e8f5e9;
          border: 1px dashed #2E7D32;
          padding: 3px 10px;
          border-radius: 6px;
          letter-spacing: 0.8px;
        }
        .btn-copy-code {
          background: transparent;
          border: 1px solid #dcdcdc;
          font-size: 0.72rem;
          font-weight: 700;
          color: #555;
          padding: 2px 8px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-copy-code:hover {
          background: #eeeeee;
          color: #111;
        }

        /* Shimmering Animated Pill for First-Time Users */
        .badge-shimmer-first-order {
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.5px;
          padding: 3px 10px;
          border-radius: 20px;
          color: white;
          background: linear-gradient(90deg, #ff8a00, #e52e71, #ff8a00);
          background-size: 200% auto;
          animation: shimmer 3s linear infinite;
          box-shadow: 0 2px 8px rgba(229, 46, 113, 0.3);
        }
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .voucher-headline {
          font-size: 1.15rem;
          font-weight: 800;
          color: #1b5e20;
          letter-spacing: 0.3px;
        }
        .voucher-max-cap {
          font-size: 0.8rem;
          font-weight: 500;
          color: #666;
          margin-left: 6px;
        }
        .voucher-description {
          font-size: 0.86rem;
          line-height: 1.35;
        }
        .voucher-meta {
          border-top: 1px dashed #e8e8e8;
          font-size: 0.75rem;
          color: #777;
        }

        .voucher-reason-banner {
          background: #fff3e0;
          border-left: 3px solid #ff9800;
          padding: 6px 10px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.78rem;
          color: #b76400;
          font-weight: 600;
        }
        .voucher-cta-footer {
          border-top: 1px solid #f0f0f0;
        }
        .badge-ineligible-tag {
          font-size: 0.76rem;
          font-weight: 700;
          color: #c62828;
          background: #ffebee;
          padding: 3px 8px;
          border-radius: 6px;
        }

        .btn-apply-voucher {
          background: #2E7D32;
          color: white;
          font-weight: 800;
          font-size: 0.82rem;
          letter-spacing: 0.6px;
          border: none;
          box-shadow: 0 2px 6px rgba(46, 125, 50, 0.25);
          transition: all 0.2s ease;
        }
        .btn-apply-voucher:hover:not(:disabled) {
          background: #1b5e20;
          transform: translateY(-1px);
        }
        .btn-apply-voucher:disabled {
          background: #cfd8dc;
          color: #78909c;
          cursor: not-allowed;
          box-shadow: none;
        }

        .btn-signin-claim {
          background: linear-gradient(135deg, #f57c00, #e65100);
          color: white;
          font-weight: 700;
          font-size: 0.8rem;
          border: none;
          box-shadow: 0 2px 8px rgba(230, 81, 0, 0.3);
          transition: all 0.2s ease;
        }
        .btn-signin-claim:hover {
          background: #d84315;
          transform: translateY(-1px);
          color: white;
        }

        /* ── Auth Prompt Modal ── */
        .auth-prompt-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          z-index: 100000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: fadeIn 0.2s ease-out;
        }
        .auth-prompt-card {
          background: #ffffff;
          border-radius: 20px;
          max-width: 440px;
          width: 100%;
          padding: 30px 26px 24px 26px;
          text-align: center;
          position: relative;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          animation: scaleUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .auth-prompt-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(90deg, #ff8a00, #e52e71);
          color: white;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.6px;
          padding: 4px 14px;
          border-radius: 20px;
          box-shadow: 0 4px 12px rgba(229, 46, 113, 0.35);
        }
        .auth-prompt-icon-wrap {
          width: 64px;
          height: 64px;
          background: #e8f5e9;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 10px auto 16px auto;
        }
        .auth-prompt-icon {
          font-size: 2rem;
        }
        .auth-prompt-title {
          font-size: 1.25rem;
          font-weight: 800;
          color: #2E7D32;
          margin-bottom: 8px;
        }
        .auth-prompt-desc {
          font-size: 0.94rem;
          color: #333;
          line-height: 1.45;
        }
        .auth-prompt-subdesc {
          margin-bottom: 0;
          line-height: 1.4;
        }
        .btn-auth-primary {
          background: #2E7D32;
          color: white;
          border: none;
          font-size: 0.95rem;
          transition: all 0.2s ease;
        }
        .btn-auth-primary:hover {
          background: #1b5e20;
          color: white;
          box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
        }
        .btn-auth-secondary {
          background: #f1f8e9;
          color: #2E7D32;
          border: 1.5px solid #2E7D32;
          font-size: 0.95rem;
          transition: all 0.2s ease;
        }
        .btn-auth-secondary:hover {
          background: #e8f5e9;
          color: #1b5e20;
        }

        /* ── Animations ── */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleUp {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PromoCodeSection;
